/**
 * Viewport de zoom/pan do modo Comparar (E36.F2/T01 + E37.F1). // ux-neutrality:allow doc-comment
 *
 * Wrapper client-side puro, apresentação-only e efêmero (mesma classe dos // ux-neutrality:allow doc-comment
 * filtros de inspeção, ADR-0039 §A): aplica `translate(pan) scale(zoom)` no // ux-neutrality:allow doc-comment
 * conteúdo via CSS transform. Tudo que está DENTRO (imagem, overlay SVG de // ux-neutrality:allow doc-comment
 * marcações, camada da régua) acompanha o transform de graça — o overlay segue // ux-neutrality:allow doc-comment
 * colado na imagem em qualquer nível de zoom. // ux-neutrality:allow doc-comment
 *
 * E37.F1: componente CONTROLADO — o estado sobe ao CompareView (sincronização // ux-neutrality:allow doc-comment
 * K↔Q vive lá); o viewport aplica gestos e devolve via `onStateChange`. // ux-neutrality:allow doc-comment
 *
 * Gestos (estilo editor de imagem): roda do mouse = zoom centrado no cursor; // ux-neutrality:allow doc-comment
 * arrasto = pan (desabilitado com a régua/desenho ativos — o arrasto é deles); // ux-neutrality:allow doc-comment
 * ESPAÇO + arrasto = pan temporário com prioridade sobre qualquer ferramenta // ux-neutrality:allow doc-comment
 * (overlay captura o gesto; soltar espaço devolve a ferramenta); botão do meio // ux-neutrality:allow doc-comment
 * pana sempre; duplo clique = caber na tela. O pan é CLAMPADO — uma margem // ux-neutrality:allow doc-comment
 * mínima de conteúdo permanece visível (não dá para "perder" a assinatura). // ux-neutrality:allow doc-comment
 *
 * Defensibilidade: zoom/pan NUNCA viram medida — a régua converte px de tela // ux-neutrality:allow doc-comment
 * em px da imagem descontando o fator de render (ver ContestedInspectionFilters). // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';

export interface ZoomPanState {
  zoom: number;
  panX: number;
  panY: number;
}

export const ZOOM_PAN_IDENTITY: ZoomPanState = { zoom: 1, panX: 0, panY: 0 };
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 8;
/** Fator por "tique" da roda (suave o bastante p/ marcação fina). */
export const WHEEL_ZOOM_STEP = 1.2;
/** Mínimo de conteúdo (px de tela) que o clamp mantém visível em cada eixo. */
export const PAN_VISIBLE_MARGIN = 64;

export function clampZoom(zoom: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
}

/**
 * Aplica um fator de zoom mantendo o ponto (cx, cy) — coordenadas locais do
 * viewport — fixo sob o cursor (zoom centrado no cursor, estilo editor de
 * imagem). Derivação: o ponto de conteúdo sob o cursor é `(cx - pan) / zoom`;
 * impor que ele continue sob o cursor após o novo zoom dá o novo pan.
 */
export function zoomAtPoint(
  state: ZoomPanState,
  factor: number,
  cx: number,
  cy: number,
): ZoomPanState {
  const zoom = clampZoom(state.zoom * factor);
  const ratio = zoom / state.zoom;
  return {
    zoom,
    panX: cx - (cx - state.panX) * ratio,
    panY: cy - (cy - state.panY) * ratio,
  };
}

/**
 * Clampa o pan para que pelo menos `PAN_VISIBLE_MARGIN` px de conteúdo
 * permaneçam visíveis em cada eixo (E37.F1.T01 — não dá para arrastar a
 * assinatura inteira para fora da tela). O box de conteúdo tem o tamanho do
 * viewport (transform-origin 0 0): após a escala ele ocupa `w*zoom × h*zoom`.
 * Dimensões desconhecidas (jsdom, primeiro render) ⇒ passthrough honesto.
 */
export function clampPan(state: ZoomPanState, width: number, height: number): ZoomPanState {
  if (!width || !height) return state;
  const clampAxis = (value: number, span: number): number => {
    const min = PAN_VISIBLE_MARGIN - span * state.zoom;
    const max = span - PAN_VISIBLE_MARGIN;
    if (min > max) return (min + max) / 2;
    return Math.min(max, Math.max(min, value));
  };
  return {
    zoom: state.zoom,
    panX: clampAxis(state.panX, width),
    panY: clampAxis(state.panY, height),
  };
}

/** Comando global (toolbar/atalhos) aplicado aos viewports; `tick` força
 * reaplicação ao repetir a ação. `percent` só acompanha `action: 'percent'`. */
export interface ZoomCommand {
  action: 'fit' | 'hundred' | 'in' | 'out' | 'percent';
  percent?: number | undefined;
  tick: number;
}

export interface ZoomPanViewportProps {
  children: React.ReactNode;
  testId: string;
  /** Arrasto com botão esquerdo pana? `false` com régua/desenho ativos (o
   * arrasto é da ferramenta); botão do meio e espaço panam sempre. */
  panDraggable: boolean;
  /** Comando vindo da toolbar/atalhos; aplicado quando `tick` muda. */
  command: ZoomCommand | null;
  /** Estado controlado (E37.F1.T03 — o CompareView sincroniza K↔Q). */
  state: ZoomPanState;
  onStateChange: (next: ZoomPanState) => void;
  /** Espaço pressionado: pan temporário com prioridade sobre as ferramentas. */
  spacePan?: boolean;
}

/** Fator p/ 100%: 1 px da imagem = 1 px de tela (descontando o object-contain). */
function hundredZoomFor(container: HTMLElement | null): number {
  const img = container?.querySelector('img');
  if (!img || !img.naturalWidth || !img.clientWidth || !img.clientHeight) return 1;
  const s = Math.min(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight);
  if (!Number.isFinite(s) || s <= 0) return 1;
  return clampZoom(1 / s);
}

/** Presets absolutos: caber (identidade) e % relativo ao natural da imagem. */
function presetState(command: ZoomCommand, el: HTMLElement | null): ZoomPanState {
  if (command.action === 'fit') return ZOOM_PAN_IDENTITY;
  const ratio = command.action === 'percent' ? (command.percent ?? 100) / 100 : 1;
  return { ...ZOOM_PAN_IDENTITY, zoom: clampZoom(hundredZoomFor(el) * ratio) };
}

/** Estado resultante de um comando da toolbar/atalhos sobre o viewport. */
function stateForCommand(
  command: ZoomCommand,
  el: HTMLElement | null,
  current: ZoomPanState,
): ZoomPanState {
  if (command.action !== 'in' && command.action !== 'out') return presetState(command, el);
  const rect = el?.getBoundingClientRect();
  const factor = command.action === 'in' ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP;
  const next = zoomAtPoint(current, factor, (rect?.width ?? 0) / 2, (rect?.height ?? 0) / 2);
  return clampPan(next, el?.clientWidth ?? 0, el?.clientHeight ?? 0);
}

export function ZoomPanViewport({
  children,
  testId,
  panDraggable,
  command,
  state,
  onStateChange,
  spacePan = false,
}: ZoomPanViewportProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  // Tick de carga da imagem: o badge de % (relativo ao tamanho natural) só é
  // calculável depois do load — sem layout novo, só re-render.
  const [, setImgTick] = useState(0);

  // Refs lidas pelos listeners nativos/efeitos sem re-registrar a cada render.
  const stateRef = useRef(state);
  stateRef.current = state;
  const onChangeRef = useRef(onStateChange);
  onChangeRef.current = onStateChange;
  const spacePanRef = useRef(spacePan);
  spacePanRef.current = spacePan;

  function emit(next: ZoomPanState): void {
    const el = containerRef.current;
    onChangeRef.current(clampPan(next, el?.clientWidth ?? 0, el?.clientHeight ?? 0));
  }

  // Comando global da toolbar/atalhos: "caber" = identidade (o layout já cabe
  // via object-contain); "100%"/presets = escala relativa ao natural da imagem;
  // in/out = um tique de roda centrado no meio do viewport.
  useEffect(() => {
    if (command === null) return;
    onChangeRef.current(stateForCommand(command, containerRef.current, stateRef.current));
  }, [command]);

  // Wheel nativo (non-passive) — o onWheel do React é passive e não permite
  // preventDefault, e sem ele a página rola junto com o gesto de zoom.
  useEffect(() => {
    const el = containerRef.current;
    if (el === null) return undefined;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = event.clientX - rect.left;
      const cy = event.clientY - rect.top;
      const factor = event.deltaY < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP;
      const next = zoomAtPoint(stateRef.current, factor, cx, cy);
      onChangeRef.current(clampPan(next, el.clientWidth, el.clientHeight));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  function onMouseDown(event: React.MouseEvent): void {
    const middle = event.button === 1;
    const leftPan = event.button === 0 && (panDraggable || spacePanRef.current);
    if (!middle && !leftPan) return;
    if (middle) event.preventDefault();
    dragRef.current = { x: event.clientX, y: event.clientY };
  }

  function onMouseMove(event: React.MouseEvent): void {
    const last = dragRef.current;
    if (last === null) return;
    const dx = event.clientX - last.x;
    const dy = event.clientY - last.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    const current = stateRef.current;
    emit({ ...current, panX: current.panX + dx, panY: current.panY + dy });
  }

  function endDrag(): void {
    dragRef.current = null;
  }

  const zoomed = state.zoom !== 1 || state.panX !== 0 || state.panY !== 0;
  // Badge: % relativo ao tamanho natural da imagem (100% = 1px imagem : 1px
  // tela), coerente com os presets da toolbar. Sem imagem mensurável (jsdom,
  // pré-load) o fator cai em 1 e o badge mostra o zoom relativo ao "caber".
  const percent = Math.round((state.zoom / hundredZoomFor(containerRef.current)) * 100);
  const containerClass = spacePan
    ? 'zoompan-viewport zoompan-viewport--space'
    : panDraggable
      ? 'zoompan-viewport zoompan-viewport--pannable'
      : 'zoompan-viewport';

  return (
    // Gestos de mouse sem semântica interativa nativa (mesmo padrão justificado
    // da RulerLayer); teclado tem o grupo de zoom da toolbar + atalhos.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={containerClass}
      data-testid={testId}
      data-zoom={Math.round(state.zoom * 1000) / 1000}
      title={ptBR.workspaceCaso.zoomGestureTitle}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onDoubleClick={() => onChangeRef.current(ZOOM_PAN_IDENTITY)}
      onLoadCapture={() => setImgTick((t) => t + 1)}
    >
      <div
        className="zoompan-content"
        style={
          zoomed
            ? { transform: `translate(${state.panX}px, ${state.panY}px) scale(${state.zoom})` }
            : undefined
        }
      >
        {children}
      </div>
      {/* Espaço pressionado: overlay captura o mousedown ANTES da régua/desenho
          (prioridade temporária do pan); os eventos borbulham para os handlers
          do container, então o arrasto pana normalmente. */}
      {spacePan && (
        <div
          className="zoompan-pan-overlay"
          data-testid={`${testId}-pan-overlay`}
          aria-hidden="true"
        />
      )}
      <span className="zoompan-badge" data-testid={`${testId}-badge`}>
        {percent}%
      </span>
    </div>
  );
}
